import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NaturalLanguageQuery = ({ onQuery, isLoading, selectedDocumentBase }) => {
  const [query, setQuery] = useState('');
  const [queryHistory, setQueryHistory] = useState([]);
  const [isProcessingQuery, setIsProcessingQuery] = useState(false);

  const suggestedQueries = [
    "What inconsistencies exist between policy documents?",
    "Show me all grammar issues in the employee handbook",
    "Are there any contradictions in the safety procedures?",
    "What gaps were detected in the compliance documentation?",
    "Which documents have the most critical issues?",
    "Show me resolved issues from the last week",
    "What are the most common spelling errors?",
    "Tell me about document quality trends",
    "Which document base needs the most attention?",
    "What types of issues are most frequent?"
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query?.trim() || isProcessingQuery || isLoading) return;

    console.log('Processing query:', query, 'with document base:', selectedDocumentBase);
    
    setIsProcessingQuery(true);
    
    const newQuery = {
      id: Date.now(),
      question: query,
      answer: "Processing your query...",
      timestamp: new Date()?.toLocaleString(),
      isProcessing: true
    };

    setQueryHistory(prev => [newQuery, ...prev]);
    
    try {
      console.log('Calling onQuery function...');
      const response = await onQuery(query, selectedDocumentBase);
      console.log('Received response:', response);
      
      setQueryHistory(prev => 
        prev?.map(item => 
          item?.id === newQuery?.id 
            ? { ...item, answer: response || "No response received from AI service.", isProcessing: false }
            : item
        )
      );
    } catch (error) {
      console.error('Query processing error:', error);
      setQueryHistory(prev => 
        prev?.map(item => 
          item?.id === newQuery?.id 
            ? { 
                ...item, 
                answer: `Error processing query: ${error?.message || 'Unknown error occurred'}. This might be due to AI service configuration issues, API limits, or connectivity problems. Please check your OpenAI API key and try again.`, 
                isProcessing: false,
                hasError: true
              }
            : item
        )
      );
    } finally {
      setIsProcessingQuery(false);
    }

    setQuery('');
  };

  const handleSuggestedQuery = async (suggestedQuery) => {
    if (isProcessingQuery || isLoading) return;
    
    console.log('Processing suggested query:', suggestedQuery);
    
    setQuery(suggestedQuery);
    setIsProcessingQuery(true);
    
    // Auto-submit suggested query
    const newQuery = {
      id: Date.now(),
      question: suggestedQuery,
      answer: "Processing your query...",
      timestamp: new Date()?.toLocaleString(),
      isProcessing: true
    };

    setQueryHistory(prev => [newQuery, ...prev]);
    
    try {
      const response = await onQuery(suggestedQuery, selectedDocumentBase);
      console.log('Suggested query response:', response);
      
      setQueryHistory(prev => 
        prev?.map(item => 
          item?.id === newQuery?.id 
            ? { ...item, answer: response || "No response received from AI service.", isProcessing: false }
            : item
        )
      );
    } catch (error) {
      console.error('Suggested query processing error:', error);
      setQueryHistory(prev => 
        prev?.map(item => 
          item?.id === newQuery?.id 
            ? { 
                ...item, 
                answer: `Error processing query: ${error?.message || 'Unknown error'}. Please try again later or check your AI service configuration.`, 
                isProcessing: false,
                hasError: true
              }
            : item
        )
      );
    } finally {
      setIsProcessingQuery(false);
    }

    setQuery('');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="MessageSquare" size={20} className="text-accent" />
        </div>
        <div>
          <h2 className="text-lg font-medium text-foreground">Ask Zentri</h2>
          <p className="text-sm text-muted-foreground">Ask AI questions about your documents and analysis results</p>
        </div>
      </div>

      {/* Processing Status Alert */}
      {isProcessingQuery && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Icon name="Loader2" size={16} className="text-blue-600 animate-spin" />
            <p className="text-sm font-medium text-blue-800">Processing your query...</p>
          </div>
          <p className="text-xs text-blue-700 mt-1">
            Your response will appear in the "Recent Queries" section below once processing is complete.
          </p>
        </div>
      )}

      {/* Query Input */}
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="flex gap-3">
          <div className="flex-1">
            <textarea
              placeholder="Ask me anything about your documents or analysis results..."
              value={query}
              onChange={(e) => setQuery(e?.target?.value)}
              disabled={isProcessingQuery || isLoading}
              rows={4}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex flex-col justify-end">
            <Button
              type="submit"
              disabled={!query?.trim() || isProcessingQuery || isLoading}
              loading={isProcessingQuery}
              iconName="Send"
              iconPosition="right"
              iconSize={16}
            >
              Ask
            </Button>
          </div>
        </div>
      </form>

      {/* Suggested Queries */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-foreground mb-3">Suggested Questions</h3>
        <div className="grid grid-cols-1 gap-2">
          {suggestedQueries?.map((suggestedQuery, index) => (
            <button
              key={index}
              onClick={() => handleSuggestedQuery(suggestedQuery)}
              disabled={isProcessingQuery || isLoading}
              className="text-left px-3 py-2 text-sm bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {suggestedQuery}
            </button>
          ))}
        </div>
      </div>

      {/* Query History */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-foreground">Recent Queries</h3>
          {queryHistory?.length > 0 && (
            <div className="text-xs text-muted-foreground">
              {queryHistory?.filter(q => q?.isProcessing)?.length > 0 && (
                <span className="text-blue-600">
                  Processing {queryHistory?.filter(q => q?.isProcessing)?.length} queries...
                </span>
              )}
            </div>
          )}
        </div>
        
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {queryHistory?.length > 0 ? (
            queryHistory?.map((item) => (
              <div key={item?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={16} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{item?.question}</p>
                    <p className="text-xs text-muted-foreground mt-1">{item?.timestamp}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    {item?.isProcessing ? (
                      <Icon name="Loader2" size={16} className="text-accent animate-spin" />
                    ) : (
                      <Icon name="Bot" size={16} className="text-accent" />
                    )}
                  </div>
                  <div className={`flex-1 rounded-lg p-3 ${item?.hasError ? 'bg-destructive/10 border border-destructive/20' : 'bg-muted/50'}`}>
                    <div className="text-sm text-foreground whitespace-pre-line">
                      {item?.answer}
                    </div>
                    {item?.hasError && (
                      <div className="mt-2 text-xs text-destructive">
                        <Icon name="AlertTriangle" size={12} className="inline mr-1" />
                        AI service error - check configuration
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-2">
                No queries yet. Ask me about your documents!
              </p>
              <p className="text-xs text-muted-foreground">
                Your responses will appear here after you submit a question above.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Notice */}
      {!import.meta.env?.VITE_OPENAI_API_KEY && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-800">AI Configuration Required</p>
              <p className="text-xs text-yellow-700 mt-1">
                To enable AI-powered document analysis, please add your OpenAI API key to the VITE_OPENAI_API_KEY environment variable.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* OpenAI Quota Notice */}
      {import.meta.env?.VITE_OPENAI_API_KEY && queryHistory?.some(item => item?.hasError) && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">AI Service Issues Detected</p>
              <p className="text-xs text-red-700 mt-1">
                Some queries failed due to OpenAI quota limits or configuration issues. 
                <br />• Check your OpenAI account billing and usage
                <br />• Verify your API key has proper permissions
                <br />• Try simpler queries while issues are resolved
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Fallback Query Examples */}
      {queryHistory?.some(item => item?.hasError) && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Lightbulb" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">Try These Simple Queries</p>
              <div className="mt-2 space-y-1">
                <button
                  onClick={() => handleSuggestedQuery('Show me a summary of all issues')}
                  className="block text-xs text-blue-700 hover:text-blue-800 underline"
                  disabled={isProcessingQuery || isLoading}
                >
                  • Show me a summary of all issues
                </button>
                <button
                  onClick={() => handleSuggestedQuery('What are the critical issues?')}
                  className="block text-xs text-blue-700 hover:text-blue-800 underline"
                  disabled={isProcessingQuery || isLoading}
                >
                  • What are the critical issues?
                </button>
                <button
                  onClick={() => handleSuggestedQuery('Give me recommendations')}
                  className="block text-xs text-blue-700 hover:text-blue-800 underline"
                  disabled={isProcessingQuery || isLoading}
                >
                  • Give me recommendations
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug Information (only in development) */}
      {import.meta.env?.DEV && (
        <div className="mt-4 p-2 bg-gray-100 border rounded text-xs">
          <p><strong>Debug Info:</strong></p>
          <p>Selected Document Base: {selectedDocumentBase}</p>
          <p>OpenAI Key Available: {import.meta.env?.VITE_OPENAI_API_KEY ? 'Yes' : 'No'}</p>
          <p>Query History Count: {queryHistory?.length}</p>
          <p>Is Processing: {isProcessingQuery ? 'Yes' : 'No'}</p>
        </div>
      )}
    </div>
  );
};

export default NaturalLanguageQuery;